import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { LANGUAGE_SELECT_OPTIONS } from "@constants/language-select-options";
import { resources } from "@constants/hardcoded-strings";
import {
    VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES,
    VerificationServiceDetailsReferenceData
} from "@constants/reference-data";
import { VERIFICATION_SERVICES_DETAILS_URL, VERIFICATION_SERVICES_URL } from "@constants/routes";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ServiceEndpointTypes } from "@enums/service-endpoint-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { formatRpServiceConfiguration } from "@formatters/rp-services/rp-service-configuration-formatter";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { RpServiceChangeRequest } from "@idw-types/data/rp-service-change-request";
import { FormattedRpServiceConfiguration, RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import {
    setTableSettingsTableFilters,
    setTableSettingsTablePagination,
    setTableSettingsTableSortBy
} from "@redux/actions/table-settings-actions";
import { setVerificationServiceDetails } from "@redux/actions/verification-service-details-actions";
import {
    getVerificationServiceDetailsReduxRequest,
    updateVerificationServiceDetailsReduxRequest
} from "@redux-requests/verification-service-details";
import {
    getCommonReferenceDataReduxRequest
} from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { injectRpServiceId } from "@utils/parameter-injector";
import { navigateToUtil } from "@utils/navigation-util";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { LoadingIndicator } from "../common/loading-indicator/loading-indicator";
import { CardContainer } from "../common/card-container/card-container";
import { ContentPageHeader } from "../common/content-page-header/content-page-header";
import { ContentPageBody } from "../common/content-page-body/content-page-body";
import { SelectFormGroup } from "../common/select-form-group/select-form-group";
import { VerificationServiceDetailsComponent } from "./verification-service-details-tab-container/verification-service-details-component";
import styles from "./verification-service-details-container.less";
import { AxnReferenceDataTypes } from "@enums/axn-reference-data-types";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";
import { getApAssertionStatusMaps } from "@utils/data-util";
import { ColumnFilter, ColumnSort } from "@tanstack/react-table";
import { getSessionState } from "@redux/selectors/root-selectors";
import { getVerificationServiceDetailsDataState } from "@redux/selectors/verification-service-details-selectors";

export const VerificationServiceDetailsContainer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { rpServiceId } = useParams<{ rpServiceId: string }>();
    const [searchParams] = useSearchParams();

    const { session } = useSelector(getSessionState);
    const {
        isLoadingVerificationServiceDetails,
        verificationServiceDetails
    } = useSelector(getVerificationServiceDetailsDataState);
    const referenceData = useReferenceDataSelector<VerificationServiceDetailsReferenceData>(VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES, true)

    const [selectedLanguage, setSelectedLanguage] = useState(LocaleTypes.ENGLISH);
    const [availableLanguages, setAvailableLanguages] = useState<Array<LocaleTypes>>([]);
    const [shouldLoadDetailsReferenceData, setShouldLoadDetailsReferenceData] = useState(true);
    const [shouldLoadVerificationServiceDetails, setShouldLoadVerificationServiceDetails] = useState(true);
    const [unformattedVerificationServiceDetails, setUnformattedVerificationServiceDetails] = useState<RpServiceConfiguration>(null);
    const [rpServiceChangeRequests, setRpServiceChangeRequests] = useState<Array<RpServiceChangeRequest>>([]);

    const tableSettingsTableType = TableSettingsTableTypes.VERIFICATION_SERVICES

    const redirectToServicesPage = () => {
        navigateToUtil(VERIFICATION_SERVICES_URL, navigate, routerLocation.pathname);
    }

    const getUnformattedVerificationServiceDetails = async (showError: boolean) => {
        const response = await getVerificationServiceDetailsReduxRequest(dispatch)(rpServiceId, redirectToServicesPage, showError);

        if (response) {
            const unformattedRelyingPartyID = unformattedVerificationServiceDetails ? unformattedVerificationServiceDetails.relyingPartyID : null;
            setUnformattedVerificationServiceDetails(response.verificationServiceDetails);
            setRpServiceChangeRequests(response.rpServiceChangeRequests);

            if (unformattedRelyingPartyID && unformattedRelyingPartyID !== unformattedVerificationServiceDetails.relyingPartyID) {
                setShouldLoadDetailsReferenceData(true);
            }
        }
    };

    useEffect(() => {
        let hasParams = false;
        if (searchParams.has("tableFilters")) {
            hasParams = true;
            try {
                const tableFilters = JSON.parse(searchParams.get("tableFilters")) as Array<ColumnFilter>;
                dispatch(setTableSettingsTableFilters(tableSettingsTableType, tableFilters, true));
            } catch (error) {
                console.error("Error parsing tableFilters from query params", error);
            }
        }
        if (searchParams.has("sortBy")) {
            hasParams = true;
            try {
                const sortBy = JSON.parse(searchParams.get("sortBy")) as Array<ColumnSort>;
                dispatch(setTableSettingsTableSortBy(tableSettingsTableType, sortBy, true));
            } catch (error) {
                console.error("Error parsing sortBy from query params", error)
            }
        }

        let pageSize;
        if (searchParams.has("pageSize")) {
            hasParams = true;
            try {
                pageSize = parseInt(searchParams.get("pageSize"));
            } catch (error) {
                console.error("Error parsing pageSize from query params", error)
            }
        }

        let pageIndex;
        if (searchParams.has("pageIndex")) {
            hasParams = true;
            try {
                pageIndex = parseInt(searchParams.get("pageIndex"));
            } catch (error) {
                console.error("Error parsing pageIndex from query params", error)
            }
        }

        let pagination = {};
        let shouldUpdatePagination = false;
        if (typeof pageSize !== "undefined") {
            pagination = { pageSize };
            shouldUpdatePagination = true;
        }
        if (typeof pageIndex !== "undefined") {
            pagination = { ...pagination, pageIndex };
            shouldUpdatePagination = true;
        }

        if (shouldUpdatePagination) {
            dispatch(setTableSettingsTablePagination(tableSettingsTableType, pagination, true));
        }
        
        if (hasParams) {
            const url = injectRpServiceId(VERIFICATION_SERVICES_DETAILS_URL, rpServiceId);
            navigateToUtil(url, navigate, routerLocation.pathname);
        }
    }, []);

    useEffect(() => {
        if (!isLoadingVerificationServiceDetails && shouldLoadVerificationServiceDetails) {
            setShouldLoadVerificationServiceDetails(false);
            getUnformattedVerificationServiceDetails(true);
        }
    }, []);

    useEffect(() => {
        if (
            unformattedVerificationServiceDetails &&
            unformattedVerificationServiceDetails.id === rpServiceId &&
            !shouldLoadVerificationServiceDetails &&
            !referenceData.isLoading.verificationServiceDetails &&
            shouldLoadDetailsReferenceData
        ) {
            setShouldLoadDetailsReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.VERIFICATION_SERVICE_DETAILS,
                true,
                ServiceEndpointTypes.OIDC,
                unformattedVerificationServiceDetails.relyingPartyID
            );
        }
    }, [
        unformattedVerificationServiceDetails,
        shouldLoadVerificationServiceDetails,
        referenceData.isLoading.verificationServiceDetails,
        shouldLoadDetailsReferenceData,
        rpServiceId
    ]);

    const isDetailsReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnPropertyTypes &&
            referenceData.axnServiceOfferings &&
            referenceData.rpServiceAttributeProperties &&
            referenceData.rpServiceAttributePropertyMaps &&
            referenceData.serviceOfferingRefreshRates &&
            referenceData.serviceOfferingRefreshRateDefault &&
            referenceData.axnAssertionStatusMaps &&
            referenceData.assertionStatusDefaults &&
            referenceData.assertionScoreAssertionStatusValueMaps &&
            referenceData.trustScoreAssertionStatusValueMaps &&
            referenceData.scoreAttributeMaps &&
            referenceData.assertionScoreServiceOfferings &&
            referenceData.trustScoreServiceOfferings &&
            referenceData.axnAssertionAttributes &&
            referenceData.axnAvailablePropertyCategories &&
            referenceData.axnIdentityProviders &&
            referenceData.axnRelyingParties &&
            referenceData.axnAttributeTypes &&
            referenceData.axnAttributeLabels &&
            referenceData.attributeClassifications &&
            referenceData.apAssertions &&
            referenceData.axnAssertions &&
            referenceData.axnAvailableAssertionStatuses &&
            referenceData.axnServiceOfferingDataSources &&
            referenceData.fallbackServiceMappings &&
            referenceData.deployedPolicyComponents &&
            referenceData.axnAttributeTypeFields &&
            referenceData.simpleDeployedPolicyComponentConditions &&
            referenceData.axnCompanies &&
            referenceData.simpleAXNWorkflows &&
            referenceData.axnAdminWorkflows &&
            referenceData.documentationLinks &&
            referenceData.axnAcquiredAttributes &&
            referenceData.messagingTemplates &&
            referenceData.axnAvailableAssertionStatuses &&
            referenceData.simpleAXNAdminRPServiceWorkflows &&
            referenceData.simpleAXNRPServiceWorkflows &&
            referenceData.availableAssertionScoreValues &&
            referenceData.availableTrustScoreValues &&
            referenceData.axnRPServices
        );
    }, [
        referenceData.axnPropertyTypes,
        referenceData.axnServiceOfferings,
        referenceData.rpServiceAttributeProperties,
        referenceData.rpServiceAttributePropertyMaps,
        referenceData.serviceOfferingRefreshRates,
        referenceData.serviceOfferingRefreshRateDefault,
        referenceData.axnAssertionStatusMaps,
        referenceData.assertionStatusDefaults,
        referenceData.assertionScoreAssertionStatusValueMaps,
        referenceData.trustScoreAssertionStatusValueMaps,
        referenceData.scoreAttributeMaps,
        referenceData.assertionScoreServiceOfferings,
        referenceData.trustScoreServiceOfferings,
        referenceData.axnAssertionAttributes,
        referenceData.axnAvailablePropertyCategories,
        referenceData.axnIdentityProviders,
        referenceData.axnRelyingParties,
        referenceData.axnAttributeTypes,
        referenceData.axnAttributeLabels,
        referenceData.attributeClassifications,
        referenceData.apAssertions,
        referenceData.axnAssertions,
        referenceData.axnAvailableAssertionStatuses,
        referenceData.axnServiceOfferingDataSources,
        referenceData.fallbackServiceMappings,
        referenceData.deployedPolicyComponents,
        referenceData.axnAttributeTypeFields,
        referenceData.simpleDeployedPolicyComponentConditions,
        referenceData.axnCompanies,
        referenceData.simpleAXNWorkflows,
        referenceData.axnAdminWorkflows,
        referenceData.documentationLinks,
        referenceData.axnAcquiredAttributes,
        referenceData.messagingTemplates,
        referenceData.axnAvailableAssertionStatuses,
        referenceData.simpleAXNAdminRPServiceWorkflows,
        referenceData.simpleAXNRPServiceWorkflows,
        referenceData.availableAssertionScoreValues,
        referenceData.availableTrustScoreValues,
        referenceData.axnRPServices
    ]);

    useEffect(() => {
        if (
            unformattedVerificationServiceDetails &&
            isDetailsReferenceDataPresent &&
            !shouldLoadDetailsReferenceData &&
            !referenceData.isLoading.verificationServiceDetails &&
            !shouldLoadVerificationServiceDetails
        ) {
            const formattedRpServiceConfiguration = formatRpServiceConfiguration(
                unformattedVerificationServiceDetails,
                referenceData.axnIdentityProviders,
                referenceData.axnRelyingParties,
                referenceData.axnAvailablePropertyCategories,
                referenceData.axnServiceOfferings,
                referenceData.axnServiceOfferingDataSources,
                referenceData.fallbackServiceMappings,
                referenceData.axnAttributeTypes,
                referenceData.axnAttributeLabels,
                referenceData.attributeClassifications,
                referenceData.apAssertions,
                referenceData.axnAssertions,
                referenceData.deployedPolicyComponents,
                referenceData.axnAttributeTypeFields,
                referenceData.rpServiceAttributeProperties,
                referenceData.rpServiceAttributePropertyMaps,
                apAssertionStatusMaps,
                referenceData.assertionScoreServiceOfferings,
                referenceData.trustScoreServiceOfferings,
                referenceData.axnCompanies
            );
            dispatch(setVerificationServiceDetails(formattedRpServiceConfiguration));
        }
    }, [
        unformattedVerificationServiceDetails,
        isDetailsReferenceDataPresent,
        shouldLoadDetailsReferenceData,
        referenceData.isLoading.verificationServiceDetails,
        shouldLoadVerificationServiceDetails
    ]);

    const isLoading = useMemo(() => {
        return (
            isLoadingVerificationServiceDetails ||
            referenceData.isLoading.verificationServiceDetails ||
            shouldLoadDetailsReferenceData ||
            shouldLoadVerificationServiceDetails
        );
    }, [
        referenceData.isLoading.verificationServiceDetails,
        isLoadingVerificationServiceDetails,
        shouldLoadDetailsReferenceData,
        shouldLoadVerificationServiceDetails
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            verificationServiceDetails &&
            isDetailsReferenceDataPresent
        );
    }, [
        isLoading,
        verificationServiceDetails,
        isDetailsReferenceDataPresent
    ]);

    const handleSave = async (rpServiceConfiguration: FormattedRpServiceConfiguration): Promise<FormattedRpServiceConfiguration> => {
        const result = await updateVerificationServiceDetailsReduxRequest(dispatch)(
            rpServiceConfiguration,
            referenceData.axnIdentityProviders,
            referenceData.axnRelyingParties,
            referenceData.axnAvailablePropertyCategories,
            referenceData.axnServiceOfferings,
            referenceData.axnServiceOfferingDataSources,
            referenceData.fallbackServiceMappings,
            referenceData.axnAttributeTypes,
            referenceData.axnAttributeLabels,
            referenceData.attributeClassifications,
            referenceData.apAssertions,
            referenceData.axnAssertions,
            referenceData.deployedPolicyComponents,
            referenceData.axnAttributeTypeFields,
            referenceData.rpServiceAttributeProperties,
            referenceData.rpServiceAttributePropertyMaps,
            apAssertionStatusMaps,
            referenceData.assertionScoreServiceOfferings,
            referenceData.trustScoreServiceOfferings,
            referenceData.axnCompanies,
            availableLanguages
        );

        return result;

    };

    const handleUpdateChangeRequest = (changeRequest: RpServiceChangeRequest) => {
        if (changeRequest.changeRequestStatus === ChangeRequestStatusTypes.CANCELED) {
            getUnformattedVerificationServiceDetails(false);
        } else if (changeRequest.changeRequestStatus === ChangeRequestStatusTypes.DEPLOYED) {
            setRpServiceChangeRequests([]);
            getCommonReferenceDataReduxRequest(dispatch, true)(
                [AxnReferenceDataTypes.SIMPLE_AXN_RP_SERVICE_WORKFLOW],
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.VERIFICATION_SERVICE_DETAILS
            );
        } else {
            setRpServiceChangeRequests([changeRequest]);
            getCommonReferenceDataReduxRequest(dispatch, true)(
                [AxnReferenceDataTypes.SIMPLE_AXN_ADMIN_RP_SERVICE_WORKFLOW],
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.VERIFICATION_SERVICE_DETAILS
            );
        }
    }

    const selectedLanguageOption = useMemo(() => {
        return LANGUAGE_SELECT_OPTIONS.find(lso => lso.value === selectedLanguage);
    }, [selectedLanguage]);

    const onChangeSelectedLanguage = (newOption: SelectOption<LocaleTypes>) => {
        if (newOption) {
            setSelectedLanguage(newOption.value);
        }
    };

    const languageSelector = useMemo(() => {
        if (isLoaded && availableLanguages.length > 0) {
            const availableLanguageSelectOptions = LANGUAGE_SELECT_OPTIONS.filter(lso => {
                return availableLanguages.includes(lso.value);
            });
            return (
                <SelectFormGroup
                    className={styles.languageSelect}
                    ariaLabel={resources.pages.verificationServiceDetails.inputs.language.label}
                    label={resources.pages.verificationServiceDetails.inputs.language.label}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.inputs.language.tooltip}
                    afterLabelTooltipTextPosition={"bottom"}
                    automationId={`verification-service-details-language-input`}
                    controlId={`verification-service-details-language-input`}
                    disabled={false}
                    isClearable={false}
                    isRequired={false}
                    isSearchable={true}
                    labelWidth={LabelWidthTypes.EXTRA_SMALL}
                    options={availableLanguageSelectOptions}
                    onChange={onChangeSelectedLanguage}
                    selectedOption={selectedLanguageOption}
                />
            );
        }

        return null;
    }, [isLoaded, selectedLanguage, availableLanguages]);

    const allowedMessagingTemplates = useMemo(() => {
        if (isLoaded && !isLoading) {
            if (verificationServiceDetails.relyingParty) {
                return referenceData.messagingTemplates.filter(mt => (
                    !mt.companyID ||
                    (mt.relyingPartyID === verificationServiceDetails.relyingParty.id) ||
                    (mt.companyID && !mt.relyingPartyID && mt.companyID === verificationServiceDetails.relyingParty.companyID)
                ));
            } else {
                return referenceData.messagingTemplates.filter(mt => !mt.companyID);
            }
        }

        return [];
    }, [isLoaded, isLoading, referenceData.messagingTemplates, verificationServiceDetails?.relyingParty]);

    const apAssertionStatusMaps: Array<ApAssertionStatusMap> = useMemo(() => {
        if (isDetailsReferenceDataPresent) {
            return getApAssertionStatusMaps(referenceData.apAssertions, referenceData.axnAssertionStatusMaps, referenceData.assertionStatusDefaults);
        }

        return [];
    }, [isDetailsReferenceDataPresent]);

    return (
        <CardContainer>
            <ContentPageHeader
                title={resources.pages.verificationServiceDetails.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"verification-service-details-page-header"}
                    />
                }
                headerButton={languageSelector}
            />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <VerificationServiceDetailsComponent
                        axnDocumentationLinks={referenceData.documentationLinks}
                        selectedLanguage={selectedLanguage}
                        availableLanguages={availableLanguages}
                        setSelectedLanguage={setSelectedLanguage}
                        setAvailableLanguages={setAvailableLanguages}
                        session={session}
                        attributeLabels={referenceData.axnAttributeLabels}
                        axnRpServices={referenceData.axnRPServices}
                        assertionAttributes={referenceData.axnAssertionAttributes}
                        axnAttributeTypes={referenceData.axnAttributeTypes}
                        axnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                        axnRelyingParties={referenceData.axnRelyingParties}
                        serviceOfferingRefreshRateDefault={referenceData.serviceOfferingRefreshRateDefault}
                        serviceOfferingRefreshRates={referenceData.serviceOfferingRefreshRates}
                        assertionStatusMaps={apAssertionStatusMaps}
                        scoreReferenceData={{
                            assertions: referenceData.axnAssertions,
                            availableAssertionStatuses: referenceData.axnAvailableAssertionStatuses,
                            assertionScoreAssertionStatusValueMaps: referenceData.assertionScoreAssertionStatusValueMaps,
                            availableAssertionScoreValues: referenceData.availableAssertionScoreValues,
                            availableTrustScoreValues: referenceData.availableTrustScoreValues,
                            trustScoreAssertionStatusValueMaps: referenceData.trustScoreAssertionStatusValueMaps
                        }}
                        assertionScoreServiceOfferingIDs={referenceData.assertionScoreServiceOfferings}
                        trustScoreServiceOfferingIDs={referenceData.trustScoreServiceOfferings}
                        scoreAttributeMaps={referenceData.scoreAttributeMaps}
                        serviceAttributeProperties={referenceData.rpServiceAttributeProperties}
                        initialRpServiceConfiguration={verificationServiceDetails}
                        simpleAXNAdminRPServiceWorkflows={referenceData.simpleAXNAdminRPServiceWorkflows}
                        simpleAXNRPServiceWorkflows={referenceData.simpleAXNRPServiceWorkflows}
                        simpleAxnWorkflows={referenceData.simpleAXNWorkflows}
                        simpleAxnAdminWorkflows={referenceData.axnAdminWorkflows}
                        axnAvailableAssertionStatuses={referenceData.axnAvailableAssertionStatuses}
                        handleSave={handleSave}
                        axnCompanies={referenceData.axnCompanies}
                        availablePropertyCategories={referenceData.axnAvailablePropertyCategories}
                        axnAcquiredAttributes={referenceData.axnAcquiredAttributes}
                        rpServiceChangeRequests={rpServiceChangeRequests}
                        messagingTemplateConfigurations={allowedMessagingTemplates}
                        axnAttributeTypeFieldCustomizations={referenceData.axnAttributeTypeFieldCustomizations}
                        setRpServiceChangeRequest={handleUpdateChangeRequest}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};
